import React from 'react'
import { Search } from 'lucide-react'
import { useThemeTokens } from '../../themes'

interface SearchInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  error?: string
  className?: string
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(({
  label,
  error,
  className = '',
  ...props
}, ref) => {
  const theme = useThemeTokens()
  
  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
  }
  
  const labelStyles: React.CSSProperties = {
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.medium,
    color: theme.colors.text,
    fontFamily: theme.typography.fontFamily,
    margin: 0,
  }
  
  const inputContainerStyles: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  }
  
  const inputStyles: React.CSSProperties = {
    width: '100%',
    padding: `${theme.spacing.sm} ${theme.spacing.sm}`,
    paddingLeft: '2.5rem', // Space for search icon
    border: `1px solid ${error ? theme.colors.error : theme.colors.border}`,
    borderRadius: theme.borderRadius.md,
    fontSize: theme.typography.fontSize.sm,
    fontFamily: theme.typography.fontFamily,
    color: theme.colors.text,
    backgroundColor: theme.colors.surface,
    outline: 'none',
    transition: 'border-color 0.2s ease',
  }
  
  const searchIconStyles: React.CSSProperties = {
    position: 'absolute',
    left: theme.spacing.sm,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    zIndex: 1
  }
  
  const errorStyles: React.CSSProperties = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.error,
    margin: 0,
  }
  
  return (
    <div style={containerStyles}>
      {label && (
        <label style={labelStyles}>
          {label}
        </label>
      )}
      <div style={inputContainerStyles}>
        <div style={searchIconStyles}>
          <Search size={16} color={theme.colors.textSecondary} />
        </div>
        <input 
          ref={ref} 
          type="text"
          style={inputStyles} 
          className={className} 
          {...props} 
        />
      </div>
      {error && (
        <p style={errorStyles}>{error}</p>
      )}
    </div>
  )
})

SearchInput.displayName = 'SearchInput' 